import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, MessagesSquare, Volume2, VolumeX, Wifi, WifiOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { Button } from "@/components/ui/button";
import ChatMessageItem from "@/components/chat/ChatMessageItem";
import ChatComposer from "@/components/chat/ChatComposer";
import { fmtDay } from "@/lib/chat";

function ChannelTabs({ channels, activeKey, onSelect }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto border-b border-border bg-slate-50/70 px-3 py-2" data-testid="chat-channel-tabs">
      {channels.map((c) => (
        <button key={c.key} type="button" onClick={() => onSelect(c.key)} data-testid={`chat-channel-${c.key}`}
          className={`relative shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
            activeKey === c.key ? "bg-brand text-white shadow-sm" : "bg-white text-slate-600 ring-1 ring-border hover:bg-brand/5"}`}>
          {c.name}
          {c.unread > 0 && (
            <span data-testid={`chat-channel-badge-${c.key}`}
              className={`ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white ${c.important > 0 ? "bg-danger animate-pulse" : "bg-danger"}`}>
              {c.unread > 99 ? "99+" : c.unread}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// Pannello chat riutilizzato da pagina e drawer.
export default function ChatPanel({ className = "" }) {
  const { user } = useAuth();
  const { channels, messages, activeKey, setActiveKey, loadMessages, markRead, send, canMarkImportant, soundOn, toggleSound, connected, registerViewer } = useChat();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const listRef = useRef(null);
  const stickBottom = useRef(true);
  const state = messages[activeKey];
  const items = useMemo(() => state?.items || [], [state]);

  useEffect(() => registerViewer(), [registerViewer]);

  useEffect(() => {
    if (!channels.some((c) => c.key === activeKey) && channels.length) setActiveKey(channels[0].key);
  }, [channels, activeKey, setActiveKey]);

  useEffect(() => {
    if (state?.loaded) return;
    setLoading(true);
    loadMessages(activeKey).catch(() => {}).finally(() => setLoading(false));
  }, [activeKey, state?.loaded, loadMessages]);

  // Lettura: i messaggi non letti del canale aperto vengono marcati letti quando visualizzati.
  const unreadIds = useMemo(() => items.filter((m) => !m.read && m.user_id !== user?.id).map((m) => m.id), [items, user?.id]);
  useEffect(() => {
    if (!unreadIds.length || document.visibilityState !== "visible") return;
    markRead(activeKey, unreadIds);
  }, [unreadIds, activeKey, markRead]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible" && unreadIds.length) markRead(activeKey, unreadIds); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [unreadIds, activeKey, markRead]);

  useEffect(() => {
    const el = listRef.current;
    if (el && stickBottom.current) el.scrollTop = el.scrollHeight;
  }, [items.length, activeKey, loading]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const loadMore = useCallback(async () => {
    if (!items.length) return;
    const el = listRef.current;
    const prevH = el?.scrollHeight || 0;
    setLoadingMore(true);
    try {
      await loadMessages(activeKey, items[0].created_at);
      requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevH; });
    } finally { setLoadingMore(false); }
  }, [items, activeKey, loadMessages]);

  const active = channels.find((c) => c.key === activeKey);

  return (
    <div className={`flex flex-col overflow-hidden rounded-xl border border-border bg-white ${className}`} data-testid="chat-panel">
      <ChannelTabs channels={channels} activeKey={activeKey} onSelect={(k) => { stickBottom.current = true; setActiveKey(k); }} />
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-1.5 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5 font-medium text-slate-700"><MessagesSquare className="h-3.5 w-3.5 text-brand" /> {active?.name || "—"}</span>
        <div className="flex items-center gap-1">
          <span className={`inline-flex items-center gap-1 ${connected ? "text-emerald-600" : "text-slate-400"}`} data-testid="chat-ws-status" title={connected ? "Connesso in tempo reale" : "Riconnessione…"}>
            {connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{connected ? "In tempo reale" : "Riconnessione…"}</span>
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={toggleSound} className="h-7 gap-1 px-2 text-xs" data-testid="chat-sound-toggle" aria-pressed={soundOn} title="Suono notifiche">
            {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />} {soundOn ? "Suono on" : "Suono off"}
          </Button>
        </div>
      </div>

      <div ref={listRef} onScroll={onScroll} className="ds-scroll flex-1 space-y-2 overflow-y-auto bg-slate-50/50 px-3 py-3" data-testid="chat-messages">
        {state?.has_more && (
          <div className="flex justify-center">
            <Button type="button" variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} className="h-7 text-xs" data-testid="chat-load-more">
              {loadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : "Carica messaggi precedenti"}
            </Button>
          </div>
        )}
        {loading && !items.length ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
        ) : !items.length ? (
          <div className="py-10 text-center text-sm text-slate-400" data-testid="chat-empty">Nessun messaggio in questo canale. Scrivi il primo!</div>
        ) : items.map((m, i) => {
          const day = fmtDay(m.created_at);
          const showDay = i === 0 || fmtDay(items[i - 1].created_at) !== day;
          return (
            <div key={m.id} className="space-y-2">
              {showDay && <div className="flex justify-center"><span className="rounded-full bg-slate-200/80 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">{day}</span></div>}
              <ChatMessageItem msg={m} mine={m.user_id === user?.id} />
            </div>
          );
        })}
      </div>

      <ChatComposer canMarkImportant={canMarkImportant} onSend={(t, imp) => { stickBottom.current = true; return send(activeKey, t, imp); }} />
    </div>
  );
}
